import React, {useState} from 'react'
import Tag from './Tag'
import {
  COUNTRY_LANG,
  REGIONS,
  USER_ROLES,
  CALL_TYPES,
} from '../../utils/constants'

export default function RuleForm({
  init,
  productGroupOptions,
  allRules,
  onSave,
  onClose,
  type,
}) {
  const isNew = !type || type !== 'edit'

  const [form, setForm] = useState(
    init || {
      language: 'English',
      country: 'US',
      region: REGIONS.US[0],
      userRole: 'Default',
      callType: Object.keys(CALL_TYPES)[0],
      productGrp: [],
      serviceId: '',
    },
  )

  const set = (k, v) => setForm(f => ({...f, [k]: v}))

  const langs = COUNTRY_LANG[form.country]?.languages || ['English']
  const isUS = form.country === 'US'

  const handleCountryChange = c => {
    set('country', c)
    set('language', (COUNTRY_LANG[c]?.languages || ['English'])[0])
    set('region', c === 'US' ? '' : 'Default')
  }

  const isDupe = allRules.some(
    r =>
      r.id !== form.id &&
      r.language === form.language &&
      r.country === form.country &&
      r.region === form.region &&
      r.userRole === form.userRole &&
      r.callType === form.callType &&
      JSON.stringify([...r.productGrp].sort()) ===
        JSON.stringify([...form.productGrp].sort()),
  )

  const ok = !isDupe && form.serviceId.trim() && form.productGrp.length > 0

  const sel = {
    width: '100%',
    padding: '7px 10px',
    fontSize: 13,
    border: '1px solid #D1D5DB',
    borderRadius: 6,
    outline: 'none',
    fontFamily: 'inherit',
    background: '#fff',
    color: '#111',
    boxSizing: 'border-box',
  }

  const selDisabled = {
    ...sel,
    background: '#F3F4F6',
    color: '#9CA3AF',
    cursor: 'not-allowed',
  }

  const lbl = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 12,
    color: '#6B7280',
    fontWeight: 500,
  }

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '11px 14px',
        }}
      >
        <label style={lbl}>
          Country
          <select
            value={form.country}
            onChange={e => handleCountryChange(e.target.value)}
            style={sel}
          >
            {Object.entries(COUNTRY_LANG).map(([key, {name}]) => (
              <option key={key} value={key}>
                {name}
              </option>
            ))}
          </select>
        </label>

        <label style={lbl}>
          Language
          <select
            value={form.language}
            disabled={!isUS}
            onChange={e => set('language', e.target.value)}
            style={isUS ? sel : selDisabled}
          >
            {langs.map(l => (
              <option key={l}>{l}</option>
            ))}
          </select>
        </label>

        <label style={lbl}>
          Region
          <select
            value={form.region}
            disabled={!isUS}
            onChange={e => set('region', e.target.value)}
            style={isUS ? sel : selDisabled}
          >
            {isUS ? (
              <>
                <option value="Default">Default</option>
                {REGIONS.US.map(r => (
                  <option key={r}>{r}</option>
                ))}
              </>
            ) : (
              <option value="Default">N/A</option>
            )}
          </select>
        </label>

        <label style={lbl}>
          User role
          <select
            value={form.userRole}
            onChange={e => set('userRole', e.target.value)}
            style={sel}
          >
            {USER_ROLES.map(r => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </label>

        <label style={{...lbl, gridColumn: '1 / -1'}}>
          Appointment type
          <select
            value={form.callType}
            onChange={e => set('callType', e.target.value)}
            style={sel}
          >
            {Object.entries(CALL_TYPES).map(([apiKey, label]) => (
              <option key={apiKey} value={apiKey}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div style={{marginTop: 11}}>
        <div style={lbl}>
          <span>
            Product groups{' '}
            <span style={{fontWeight: 400, color: '#9CA3AF'}}>
              (at least one required)
            </span>
          </span>
          <div
            style={{
              border: `1px solid ${
                form.productGrp.length === 0 ? '#FECACA' : '#D1D5DB'
              }`,
              borderRadius: 6,
              background: '#fff',
              maxHeight: 250,
              overflowY: 'auto',
            }}
          >
            {productGroupOptions.map(p => (
              <label
                key={p}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '5px 10px',
                  fontSize: 13,
                  color: '#374151',
                  borderBottom: '0.5px solid #F3F4F6',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={form.productGrp.includes(p)}
                  onChange={e =>
                    set(
                      'productGrp',
                      e.target.checked
                        ? [...form.productGrp, p]
                        : form.productGrp.filter(x => x !== p),
                    )
                  }
                  style={{accentColor: '#ff5000'}}
                />
                {p}
              </label>
            ))}
          </div>
          {form.productGrp.length === 0 && (
            <span style={{fontSize: 11, color: '#DC2626', marginTop: 2}}>
              At least one product group is required.
            </span>
          )}
        </div>
        {form.productGrp.length > 0 && (
          <div
            style={{display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5}}
          >
            {form.productGrp.map(p => (
              <Tag
                key={p}
                color="blue"
                onRemove={() =>
                  set(
                    'productGrp',
                    form.productGrp.filter(x => x !== p),
                  )
                }
              >
                {p}
              </Tag>
            ))}
          </div>
        )}
      </div>

      <label style={{...lbl, marginTop: 11}}>
        Service ID
        <input
          value={form.serviceId}
          onChange={e => set('serviceId', e.target.value)}
          placeholder="e.g. 76uzooyxaamlqy1s"
          style={{
            ...sel,
            fontFamily: 'monospace',
            letterSpacing: '0.04em',
          }}
        />
      </label>

      {isDupe && (
        <div
          style={{
            marginTop: 9,
            padding: '7px 11px',
            background: '#FEF3C7',
            borderRadius: 6,
            fontSize: 12,
            color: '#92400E',
            border: '1px solid #FDE68A',
          }}
        >
          A rule with this combination already exists.
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: '1.1rem',
        }}
      >
        <button
          onClick={onClose}
          style={{
            fontSize: 13,
            padding: '7px 15px',
            border: '1px solid #D1D5DB',
            borderRadius: 6,
            background: '#fff',
            cursor: 'pointer',
            color: '#374151',
          }}
        >
          Cancel
        </button>
        <button
          onClick={() => ok && onSave(form)}
          disabled={!ok}
          style={{
            fontSize: 13,
            padding: '7px 15px',
            borderRadius: 6,
            border: 'none',
            cursor: ok ? 'pointer' : 'default',
            background: ok ? '#ff5000' : '#F3F4F6',
            color: ok ? '#fff' : '#9CA3AF',
            fontWeight: 500,
          }}
        >
          {isNew ? 'Create rule' : 'Save changes'}
        </button>
      </div>
    </>
  )
}
